import { useEffect, useMemo, useRef, useState } from 'react';
import { Calendar, Check, ChevronDown, Search, Tag, X } from 'lucide-react';
import type { Coupon, MenuItem } from '../../App';
import { api } from '../../lib/api';

type ApplicableMode = 'all' | 'categories' | 'items';

type OfferEditorValue = {
  title: string;
  promoCode: string;
  discountType: 'percentage' | 'flat';
  discountValue: number | '';
  maxDiscount: number | '';
  minOrderValue: number | '';
  startDate: string; // YYYY-MM-DD
  endDate: string; // YYYY-MM-DD
  totalUsageLimit: number | '';
  perCustomerLimit: number | '';
  terms: string;
  isActive: boolean;
  applicableMode: ApplicableMode;
  selectedCategoryIds: string[];
  selectedItemIds: string[];
};

type FoodCategoryOption = {
  id: string;
  name: string;
  description?: string | null;
};

const toInputYmd = (value: string | undefined): string => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const dt = new Date(raw);
  if (Number.isNaN(dt.getTime())) return '';
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
};

const ymdToIsoUtc = (ymd: string): string => {
  const [y, m, d] = String(ymd || '').split('-').map((part) => Number(part));
  if (!y || !m || !d) return new Date().toISOString();
  return new Date(Date.UTC(y, m - 1, d, 0, 0, 0)).toISOString();
};

const normalizePromoCode = (value: string) =>
  String(value || '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, 20);

const makeCodeFromTitle = (title: string) => {
  const cleaned = String(title || '')
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '')
    .slice(0, 10);
  if (cleaned.length >= 4) return cleaned;
  const suffix = String(Math.floor(Math.random() * 9000) + 1000);
  return `${cleaned || 'OFFER'}${suffix}`.slice(0, 10);
};

const buildDescription = (title: string, terms: string) => {
  const safeTitle = String(title || '').trim();
  const safeTerms = String(terms || '').trim();
  if (!safeTitle && !safeTerms) return '';
  if (safeTitle && !safeTerms) return safeTitle;
  if (!safeTitle && safeTerms) return safeTerms;
  return `${safeTitle}\n\nTerms:\n${safeTerms}`;
};

const splitTitleAndTerms = (description: string) => {
  const raw = String(description || '').trim();
  if (!raw) return { title: '', terms: '' };
  const parts = raw.split(/\n\s*\nTerms:\s*\n/i);
  if (parts.length >= 2) {
    return { title: parts[0].trim(), terms: parts.slice(1).join('\n\nTerms:\n').trim() };
  }
  return { title: raw, terms: '' };
};

export function OfferEditor({
  restaurantId,
  mode,
  initialCoupon,
  onCancel,
  onSave,
}: {
  restaurantId: string;
  mode: 'create' | 'edit';
  initialCoupon?: Coupon | null;
  onCancel: () => void;
  onSave: (payload: {
    title: string;
    code: string;
    description: string;
    discountType: 'percentage' | 'flat';
    discountValue: number;
    minOrderValue: number;
    maxDiscount?: number;
    validFrom: string;
    validUntil: string;
    usageLimit?: number;
    isActive: boolean;
    meta: {
      applicableMode: ApplicableMode;
      selectedCategoryIds: string[];
      selectedItemIds: string[];
    };
  }) => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const [menuLoading, setMenuLoading] = useState(false);
  const [foodCategories, setFoodCategories] = useState<FoodCategoryOption[]>([]);
  const [items, setItems] = useState<MenuItem[]>([]);
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const [categorySearch, setCategorySearch] = useState('');
  const [dishDropdownOpen, setDishDropdownOpen] = useState(false);
  const [dishSearch, setDishSearch] = useState('');
  const categoryDropdownRef = useRef<HTMLDivElement | null>(null);
  const dishDropdownRef = useRef<HTMLDivElement | null>(null);

  const initial = useMemo<OfferEditorValue>(() => {
    const coupon = initialCoupon || null;
    const parsed = splitTitleAndTerms(coupon?.description || '');
    const now = new Date();
    const plus30 = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
    return {
      title: parsed.title,
      promoCode: coupon?.code || '',
      discountType: coupon?.discountType || 'percentage',
      discountValue: typeof coupon?.discountValue === 'number' ? coupon.discountValue : '',
      maxDiscount: typeof coupon?.maxDiscount === 'number' ? coupon.maxDiscount : '',
      minOrderValue: typeof coupon?.minOrderValue === 'number' ? coupon.minOrderValue : '',
      startDate: toInputYmd(coupon?.validFrom) || toInputYmd(now.toISOString()),
      endDate: toInputYmd(coupon?.validUntil) || toInputYmd(plus30.toISOString()),
      totalUsageLimit: typeof coupon?.usageLimit === 'number' ? coupon.usageLimit : '',
      perCustomerLimit: '',
      terms: parsed.terms,
      isActive: coupon?.isActive !== false,
      applicableMode: (coupon as any)?.applicableMode || 'all',
      selectedCategoryIds: Array.isArray((coupon as any)?.selectedCategoryIds)
        ? (coupon as any).selectedCategoryIds
        : [],
      selectedItemIds: Array.isArray((coupon as any)?.selectedItemIds) ? (coupon as any).selectedItemIds : [],
    };
  }, [initialCoupon]);

  const [value, setValue] = useState<OfferEditorValue>(initial);

  useEffect(() => setValue(initial), [initial]);

  useEffect(() => {
    let mounted = true;
    setMenuLoading(true);
    setError(null);

    const mapMenuItem = (row: any): MenuItem => ({
      id: String(row.id),
      restaurantId: String(row.restaurant_id ?? row.restaurantId ?? restaurantId),
      categoryId: String(row.category_id ?? row.categoryId ?? row.menu_category_id ?? ''),
      name: String(row.name ?? row.item_name ?? ''),
      description: String(row.description ?? ''),
      price: Number(row.price ?? row.selling_price ?? 0),
      discountPrice: row.discount_price ?? row.discountPrice ?? null,
      discountPercent: row.discount_percent ?? row.discountPercent ?? null,
      image: row.image ?? row.image_url ?? undefined,
      tags: Array.isArray(row.tags) ? row.tags.map((t: any) => String(t)) : [],
      spicyLevel: row.spicy_level ?? row.spicyLevel ?? undefined,
      isActive: Boolean(row.is_active ?? row.isActive ?? true),
      preparationTime: Number(row.preparation_time ?? row.preparationTime ?? 0),
      inStock: Boolean(row.in_stock ?? row.inStock ?? true),
      variants: Array.isArray(row.variants) ? row.variants : undefined,
      addOns: Array.isArray(row.addOns ?? row.add_ons) ? (row.addOns ?? row.add_ons) : undefined,
      customizationGroups: Array.isArray(row.customizationGroups ?? row.customization_groups)
        ? (row.customizationGroups ?? row.customization_groups)
        : undefined,
    });

    (async () => {
      try {
        const [foodCategoryRes, menuItemRes] = await Promise.all([
          (api.masterData as any).foodCategories?.getAll
            ? (api.masterData as any).foodCategories.getAll({ isActive: true })
            : Promise.resolve({ success: true, data: [] }),
          api.menu.getItems(restaurantId, undefined, { isActive: 'all' }),
        ]);

        if (!mounted) return;

        const categoryRows = Array.isArray((foodCategoryRes as any).data) ? (foodCategoryRes as any).data : [];
        setFoodCategories(
          categoryRows.map((row: any) => ({
            id: String(row.id),
            name: String(row.name ?? row.code ?? ''),
            description: row.description ?? null,
          }))
        );

        const itemRows = Array.isArray((menuItemRes as any).data) ? (menuItemRes as any).data : [];
        setItems(itemRows.map(mapMenuItem));
      } catch (e) {
        if (!mounted) return;
        setFoodCategories([]);
        setItems([]);
        setError(e instanceof Error ? e.message : 'Unable to load menu data');
      } finally {
        if (mounted) setMenuLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [restaurantId]);

  const selectedCategories = useMemo(() => {
    const source = Array.isArray(foodCategories) ? foodCategories : [];
    const ids = new Set(value.selectedCategoryIds);
    return source.filter((cat) => ids.has(cat.id));
  }, [foodCategories, value.selectedCategoryIds]);

  const selectedItems = useMemo(
    () => items.filter((it) => value.selectedItemIds.includes(it.id)),
    [items, value.selectedItemIds]
  );

  const filteredItems = useMemo(() => {
    const q = String(dishSearch || '').trim().toLowerCase();
    const base = Array.isArray(items) ? items : [];
    if (!q) return base.slice(0, 30);
    return base
      .filter((it) => String(it.name || '').toLowerCase().includes(q))
      .slice(0, 30);
  }, [items, dishSearch]);

  const filteredFoodCategories = useMemo(() => {
    const q = String(categorySearch || '').trim().toLowerCase();
    const base = Array.isArray(foodCategories) ? foodCategories : [];
    if (!q) return base.slice(0, 60);
    return base
      .filter((cat) => String(cat.name || '').toLowerCase().includes(q))
      .slice(0, 60);
  }, [foodCategories, categorySearch]);

  const categoryNameById = useMemo(() => {
    const map: Record<string, string> = {};
    for (const row of Array.isArray(foodCategories) ? foodCategories : []) {
      const id = String(row.id || '').trim();
      const name = String(row.name || '').trim();
      if (id && name) map[id] = name;
    }
    return map;
  }, [foodCategories]);

  const dishNameById = useMemo(() => {
    const map: Record<string, string> = {};
    for (const row of Array.isArray(items) ? items : []) {
      const id = String(row.id || '').trim();
      const name = String(row.name || '').trim();
      if (id && name) map[id] = name;
    }
    return map;
  }, [items]);

  const selectedCategoryNames = useMemo(
    () => value.selectedCategoryIds.map((id) => categoryNameById[String(id)]).filter(Boolean),
    [value.selectedCategoryIds, categoryNameById]
  );

  const selectedDishNames = useMemo(
    () => value.selectedItemIds.map((id) => dishNameById[String(id)]).filter(Boolean),
    [value.selectedItemIds, dishNameById]
  );

  const allVisibleCategoriesSelected = useMemo(() => {
    if (!filteredFoodCategories.length) return false;
    const selected = new Set(value.selectedCategoryIds.map((id) => String(id)));
    return filteredFoodCategories.every((cat) => selected.has(String(cat.id)));
  }, [filteredFoodCategories, value.selectedCategoryIds]);

  const allVisibleDishesSelected = useMemo(() => {
    if (!filteredItems.length) return false;
    const selected = new Set(value.selectedItemIds.map((id) => String(id)));
    return filteredItems.every((dish) => selected.has(String(dish.id)));
  }, [filteredItems, value.selectedItemIds]);

  useEffect(() => {
    const onMouseDown = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (categoryDropdownOpen) {
        const el = categoryDropdownRef.current;
        if (el && target && !el.contains(target)) setCategoryDropdownOpen(false);
      }
      if (dishDropdownOpen) {
        const el = dishDropdownRef.current;
        if (el && target && !el.contains(target)) setDishDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [categoryDropdownOpen, dishDropdownOpen, categoryDropdownRef, dishDropdownRef]);

  const toggleInList = (list: string[], id: string) => {
    if (list.includes(id)) return list.filter((x) => x !== id);
    return [...list, id];
  };

  const canSubmit = () => {
    const discountValue = Number(value.discountValue);
    const minOrderValue = Number(value.minOrderValue);
    if (!String(value.promoCode || '').trim() && !String(value.title || '').trim()) return false;
    if (!Number.isFinite(discountValue) || discountValue <= 0) return false;
    if (!Number.isFinite(minOrderValue) || minOrderValue < 0) return false;
    if (!value.startDate || !value.endDate) return false;
    return true;
  };

  const submit = () => {
    setError(null);

    const title = String(value.title || '').trim();
    const terms = String(value.terms || '').trim();
    const resolvedCode = normalizePromoCode(String(value.promoCode || '').trim()) || makeCodeFromTitle(title);
    const discountValue = Number(value.discountValue);
    const minOrderValue = Number(value.minOrderValue);
    const maxDiscountRaw = value.discountType === 'percentage' ? Number(value.maxDiscount) : Number.NaN;
    const maxDiscount = Number.isFinite(maxDiscountRaw) && maxDiscountRaw > 0 ? maxDiscountRaw : undefined;
    const usageLimitRaw = Number(value.totalUsageLimit);
    const usageLimit = Number.isFinite(usageLimitRaw) && usageLimitRaw > 0 ? usageLimitRaw : undefined;

    if (!resolvedCode) {
      setError('Please enter a promo code or offer title.');
      return;
    }
    if (!Number.isFinite(discountValue) || discountValue <= 0) {
      setError('Please enter a valid discount value.');
      return;
    }
    if (!Number.isFinite(minOrderValue) || minOrderValue < 0) {
      setError('Please enter a valid minimum order amount.');
      return;
    }
    if (!value.startDate || !value.endDate) {
      setError('Please select campaign start and end dates.');
      return;
    }

    onSave({
      title,
      code: resolvedCode,
      description: buildDescription(title, terms),
      discountType: value.discountType,
      discountValue,
      minOrderValue,
      maxDiscount,
      validFrom: ymdToIsoUtc(value.startDate),
      validUntil: ymdToIsoUtc(value.endDate),
      usageLimit,
      isActive: value.isActive,
      meta: {
        applicableMode: value.applicableMode,
        selectedCategoryIds: value.selectedCategoryIds,
        selectedItemIds: value.selectedItemIds,
      },
    });
  };

  return (
    <div className="space-y-6">
      <div className="rounded-2xl bg-gradient-to-r from-blue-800 via-blue-700 to-sky-600 p-6 text-white shadow-sm">
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div>
            <div className="text-sm text-white/80">Growth / Offers</div>
            <h1 className="mt-1 text-2xl font-semibold">{mode === 'edit' ? 'Edit Offer' : 'Create New Offer'}</h1>
            <p className="mt-1 text-white/80">Setup a new promotion for your restaurant</p>
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
        <h2 className="text-lg font-semibold text-gray-900">Offer Details</h2>
        <p className="mt-1 text-sm text-gray-600">Basic information for your offer</p>

        <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">Offer Title / Name</label>
            <input
              value={value.title}
              onChange={(e) => setValue((prev) => ({ ...prev, title: e.target.value }))}
              className="w-full rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
              placeholder="e.g., 20% OFF on Starters"
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">Promo Code</label>
            <input
              value={value.promoCode}
              onChange={(e) => setValue((prev) => ({ ...prev, promoCode: normalizePromoCode(e.target.value) }))}
              disabled={mode === 'edit'}
              className={`w-full rounded-xl border border-gray-300 px-4 py-2.5 text-sm uppercase outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-200 ${
                mode === 'edit' ? 'bg-gray-100 text-gray-600' : 'bg-white'
              }`}
              placeholder="e.g., FEST20"
              maxLength={20}
            />
            <p className="mt-2 text-xs text-gray-500">
              {mode === 'edit'
                ? 'Promo code cannot be changed after creation.'
                : 'Leave empty to auto-generate a code from the title.'}
            </p>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
        <h2 className="text-lg font-semibold text-gray-900">Discount</h2>
        <p className="mt-1 text-sm text-gray-600">Configure discount value and conditions</p>

        <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">Discount Value (% or ₹)</label>
            <div className="flex gap-2">
              <input
                type="number"
                value={value.discountValue}
                onChange={(e) =>
                  setValue((prev) => ({
                    ...prev,
                    discountValue: e.target.value === '' ? '' : Number(e.target.value),
                  }))
                }
                className="w-full rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                placeholder={value.discountType === 'percentage' ? '20' : '100'}
                min={0}
                max={value.discountType === 'percentage' ? 100 : undefined}
              />
              <div className="inline-flex overflow-hidden rounded-xl border border-gray-300 bg-gray-50">
                <button
                  type="button"
                  onClick={() => setValue((prev) => ({ ...prev, discountType: 'percentage' }))}
                  className={`px-3 py-2 text-sm font-semibold transition ${
                    value.discountType === 'percentage' ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  %
                </button>
                <button
                  type="button"
                  onClick={() => setValue((prev) => ({ ...prev, discountType: 'flat' }))}
                  className={`px-3 py-2 text-sm font-semibold transition ${
                    value.discountType === 'flat' ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  ₹
                </button>
              </div>
            </div>
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">
              Max Discount (₹) <span className="text-gray-400">(optional)</span>
            </label>
            <input
              type="number"
              value={value.maxDiscount}
              onChange={(e) =>
                setValue((prev) => ({
                  ...prev,
                  maxDiscount: e.target.value === '' ? '' : Number(e.target.value),
                }))
              }
              disabled={value.discountType !== 'percentage'}
              className={`w-full rounded-xl border border-gray-300 px-4 py-2.5 text-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-200 ${
                value.discountType !== 'percentage' ? 'bg-gray-100 text-gray-500' : 'bg-white'
              }`}
              placeholder={value.discountType === 'percentage' ? '200' : 'Disabled for flat discounts'}
              min={0}
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">Minimum Order Amount (₹)</label>
            <input
              type="number"
              value={value.minOrderValue}
              onChange={(e) =>
                setValue((prev) => ({
                  ...prev,
                  minOrderValue: e.target.value === '' ? '' : Number(e.target.value),
                }))
              }
              className="w-full rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
              placeholder="300"
              min={0}
            />
          </div>

          <div className="flex items-center justify-between rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
            <div>
              <div className="text-sm font-semibold text-gray-900">Offer Status</div>
              <div className="text-xs text-gray-600">{value.isActive ? 'Active' : 'Inactive'}</div>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={value.isActive}
              onClick={() => setValue((prev) => ({ ...prev, isActive: !prev.isActive }))}
              className={`relative inline-flex h-7 w-12 items-center rounded-full transition ${
                value.isActive ? 'bg-green-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white transition ${
                  value.isActive ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
        <h2 className="text-lg font-semibold text-gray-900">Applicable On</h2>
        <p className="mt-1 text-sm text-gray-600">Choose where this offer should apply</p>

        <div className="mt-5 space-y-4">
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:gap-6">
            {(
              [
                { id: 'all', label: 'All menu items' },
                { id: 'categories', label: 'Select Categories' },
                { id: 'items', label: 'Select Dishes' },
              ] as Array<{ id: ApplicableMode; label: string }>
            ).map((opt) => (
              <label key={opt.id} className="inline-flex cursor-pointer items-center gap-2 text-sm text-gray-700">
                <input
                  type="radio"
                  name="applicableMode"
                  checked={value.applicableMode === opt.id}
                  onChange={() => setValue((prev) => ({ ...prev, applicableMode: opt.id }))}
                  className="h-4 w-4 accent-blue-600"
                />
                {opt.label}
              </label>
            ))}
          </div>

          {value.applicableMode === 'categories' && (
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
              <div className="text-sm font-semibold text-gray-900">Select Categories</div>

              <div className="mt-3 relative" ref={categoryDropdownRef}>
                <button
                  type="button"
                  onClick={() => {
                    setCategoryDropdownOpen((prev) => !prev);
                    setDishDropdownOpen(false);
                  }}
                  className="w-full rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-left text-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-200 flex items-center justify-between"
                >
                  <span
                    className={`${value.selectedCategoryIds.length ? 'text-gray-900' : 'text-gray-500'} truncate pr-3`}
                    title={selectedCategoryNames.join(', ')}
                  >
                    {value.selectedCategoryIds.length ? selectedCategoryNames.join(', ') : 'Select categories...'}
                  </span>
                  <ChevronDown
                    className={`h-4 w-4 text-gray-500 transition-transform ${categoryDropdownOpen ? 'rotate-180' : ''}`}
                  />
                </button>

                {categoryDropdownOpen && (
                  <div className="absolute z-20 mt-2 w-full rounded-xl border border-gray-200 bg-white shadow-xl">
                    <div className="p-2 border-b border-gray-100">
                      <div className="flex items-center gap-2">
                        <label className="inline-flex items-center justify-center px-2 py-2 border border-gray-300 rounded-lg cursor-pointer select-none">
                          <input
                            type="checkbox"
                            checked={allVisibleCategoriesSelected}
                            onChange={() => {
                              const visibleIds = filteredFoodCategories.map((c) => String(c.id)).filter(Boolean);
                              if (!visibleIds.length) return;
                              setValue((prev) => {
                                const selected = new Set(prev.selectedCategoryIds.map((id) => String(id)));
                                const allSelected = visibleIds.every((id) => selected.has(id));
                                if (allSelected) {
                                  return {
                                    ...prev,
                                    selectedCategoryIds: prev.selectedCategoryIds.filter(
                                      (id) => !visibleIds.includes(String(id))
                                    ),
                                  };
                                }
                                visibleIds.forEach((id) => selected.add(id));
                                return { ...prev, selectedCategoryIds: Array.from(selected) };
                              });
                            }}
                            className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                        </label>
                        <div className="relative flex-1">
                          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                          <input
                            type="text"
                            value={categorySearch}
                            onChange={(e) => setCategorySearch(e.target.value)}
                            placeholder="Search categories..."
                            className="w-full pl-9 pr-9 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                          />
                          {categorySearch && (
                            <button
                              type="button"
                              onClick={() => setCategorySearch('')}
                              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="max-h-56 overflow-y-auto p-2 space-y-1">
                      {filteredFoodCategories.length === 0 && (
                        <div className="px-3 py-2 text-sm text-gray-500">No categories found</div>
                      )}
                      {filteredFoodCategories.map((cat) => {
                        const checked = value.selectedCategoryIds.includes(cat.id);
                        return (
                          <label
                            key={cat.id}
                            className="flex items-center gap-3 px-3 py-2 rounded-md hover:bg-blue-50 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() =>
                                setValue((prev) => ({
                                  ...prev,
                                  selectedCategoryIds: toggleInList(prev.selectedCategoryIds, cat.id),
                                }))
                              }
                              className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-sm text-gray-800">{cat.name}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {value.selectedCategoryIds.length > 0 && (
                <div className="mt-3 text-xs text-gray-600">Selected: {value.selectedCategoryIds.length}</div>
              )}
            </div>
          )}

          {value.applicableMode === 'items' && (
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
              <div className="text-sm font-semibold text-gray-900">Select Dishes</div>

              <div className="mt-3 relative" ref={dishDropdownRef}>
                <button
                  type="button"
                  onClick={() => {
                    setDishDropdownOpen((prev) => !prev);
                    setCategoryDropdownOpen(false);
                  }}
                  className="w-full rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-left text-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-200 flex items-center justify-between"
                >
                  <span
                    className={`${value.selectedItemIds.length ? 'text-gray-900' : 'text-gray-500'} truncate pr-3`}
                    title={selectedDishNames.join(', ')}
                  >
                    {value.selectedItemIds.length ? selectedDishNames.join(', ') : 'Select dishes...'}
                  </span>
                  <ChevronDown
                    className={`h-4 w-4 text-gray-500 transition-transform ${dishDropdownOpen ? 'rotate-180' : ''}`}
                  />
                </button>

                {dishDropdownOpen && (
                  <div className="absolute z-20 mt-2 w-full rounded-xl border border-gray-200 bg-white shadow-xl">
                    <div className="p-2 border-b border-gray-100">
                      <div className="flex items-center gap-2">
                        <label className="inline-flex items-center justify-center px-2 py-2 border border-gray-300 rounded-lg cursor-pointer select-none">
                          <input
                            type="checkbox"
                            checked={allVisibleDishesSelected}
                            onChange={() => {
                              const visibleIds = filteredItems.map((d) => String(d.id)).filter(Boolean);
                              if (!visibleIds.length) return;
                              setValue((prev) => {
                                const selected = new Set(prev.selectedItemIds.map((id) => String(id)));
                                const allSelected = visibleIds.every((id) => selected.has(id));
                                if (allSelected) {
                                  return {
                                    ...prev,
                                    selectedItemIds: prev.selectedItemIds.filter((id) => !visibleIds.includes(String(id))),
                                  };
                                }
                                visibleIds.forEach((id) => selected.add(id));
                                return { ...prev, selectedItemIds: Array.from(selected) };
                              });
                            }}
                            className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                        </label>
                        <div className="relative flex-1">
                          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                          <input
                            type="text"
                            value={dishSearch}
                            onChange={(e) => setDishSearch(e.target.value)}
                            placeholder="Search dishes..."
                            className="w-full pl-9 pr-9 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                          />
                          {dishSearch && (
                            <button
                              type="button"
                              onClick={() => setDishSearch('')}
                              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="max-h-56 overflow-y-auto p-2 space-y-1">
                      {filteredItems.length === 0 && (
                        <div className="px-3 py-2 text-sm text-gray-500">No dishes found</div>
                      )}
                      {filteredItems.map((dish) => {
                        const checked = value.selectedItemIds.includes(dish.id);
                        return (
                          <label
                            key={dish.id}
                            className="flex items-center justify-between gap-3 px-3 py-2 rounded-md hover:bg-blue-50 cursor-pointer"
                          >
                            <div className="flex items-center gap-3 min-w-0">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() =>
                                  setValue((prev) => ({
                                    ...prev,
                                    selectedItemIds: toggleInList(prev.selectedItemIds, dish.id),
                                  }))
                                }
                                className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <span className="truncate text-sm text-gray-800">{dish.name}</span>
                            </div>
                            <span className="text-xs text-gray-500">₹{Math.round(Number(dish.price || 0))}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {value.selectedItemIds.length > 0 && (
                <div className="mt-3 text-xs text-gray-600">Selected dishes: {value.selectedItemIds.length}</div>
              )}
            </div>
          )}

          {menuLoading && <div className="text-sm text-gray-600">Loading menu data...</div>}
        </div>
      </div>

      <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
        <h2 className="text-lg font-semibold text-gray-900">Campaign & Limits</h2>
        <p className="mt-1 text-sm text-gray-600">Schedule the offer and configure usage limits</p>

        <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">Start Date</label>
            <div className="relative">
              <Calendar className="pointer-events-none absolute right-3 top-2.5 h-4 w-4 text-gray-400" />
              <input
                type="date"
                value={value.startDate}
                onChange={(e) => setValue((prev) => ({ ...prev, startDate: e.target.value }))}
                className="w-full rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
              />
            </div>
          </div>
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">End Date</label>
            <div className="relative">
              <Calendar className="pointer-events-none absolute right-3 top-2.5 h-4 w-4 text-gray-400" />
              <input
                type="date"
                value={value.endDate}
                onChange={(e) => setValue((prev) => ({ ...prev, endDate: e.target.value }))}
                className="w-full rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
              />
            </div>
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">Usage Limit (total)</label>
            <input
              type="number"
              value={value.totalUsageLimit}
              onChange={(e) =>
                setValue((prev) => ({
                  ...prev,
                  totalUsageLimit: e.target.value === '' ? '' : Number(e.target.value),
                }))
              }
              className="w-full rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
              placeholder="e.g., 500"
              min={0}
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">
              Usage Limit (per customer) <span className="text-gray-400">(coming soon)</span>
            </label>
            <input
              type="number"
              value={value.perCustomerLimit}
              onChange={(e) =>
                setValue((prev) => ({
                  ...prev,
                  perCustomerLimit: e.target.value === '' ? '' : Number(e.target.value),
                }))
              }
              disabled
              className="w-full rounded-xl border border-gray-300 bg-gray-100 px-4 py-2.5 text-sm text-gray-500 outline-none"
              placeholder="Not available"
            />
          </div>

          <div className="md:col-span-2">
            <label className="mb-2 block text-sm font-medium text-gray-700">Terms</label>
            <textarea
              value={value.terms}
              onChange={(e) => setValue((prev) => ({ ...prev, terms: e.target.value }))}
              className="min-h-[120px] w-full resize-y rounded-xl border border-gray-300 bg-white px-4 py-3 text-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
              placeholder="e.g., Valid on weekends only. Not applicable on combos."
            />
          </div>
        </div>
      </div>

      <div className="sticky bottom-0 z-20 border-t border-slate-100 bg-white px-5 py-4">
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex items-center gap-2 rounded-xl bg-slate-100 px-5 py-2.5 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-200"
          >
            <X className="h-4 w-4" />
            Cancel
          </button>
          <button
            type="button"
            disabled={!canSubmit()}
            onClick={submit}
            className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <Check className="h-4 w-4" />
            Save &amp; Publish
          </button>
        </div>
      </div>
    </div>
  );
}
